/**
 * Loyalty React Hooks
 * Epic 68: VIP Lojalitási Program
 *
 * TanStack Query hooks for loyalty points, rewards, and partner status.
 */

import {
  earnPoints,
  getLoyaltyRewards,
  getPartnerLoyalty,
  redeemPoints,
  runYearlyReset,
  type LoyaltyReward,
  type PartnerLoyaltyStatus,
} from '@/api/loyalty';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function usePartnerLoyalty(partnerId: string | undefined) {
  const query = useQuery({
    queryKey: ['loyalty', 'partner', partnerId],
    queryFn: async () => {
      if (!partnerId) throw new Error('No partner ID');
      const res = await getPartnerLoyalty(partnerId);
      return res.data;
    },
    enabled: !!partnerId,
  });

  return {
    status: query.data as PartnerLoyaltyStatus | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useLoyaltyRewards() {
  const query = useQuery({
    queryKey: ['loyalty', 'rewards'],
    queryFn: async () => {
      const res = await getLoyaltyRewards();
      return res.data;
    },
  });

  return {
    rewards: (query.data ?? []) as LoyaltyReward[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

export function useEarnPoints(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      amount: number;
      source: 'RENTAL' | 'PURCHASE' | 'SERVICE' | 'SALES' | 'BONUS';
      // M-016: rentalCount removed — server-computed from DB rental records
      sourceId?: string;
      note?: string;
      costBearerId?: string;
    }) => earnPoints(partnerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'partner', partnerId] });
    },
  });
}

export function useRedeemPoints(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { rewardId: string }) => redeemPoints(partnerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'partner', partnerId] });
    },
  });
}

export function useYearlyReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => runYearlyReset(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty'] });
    },
  });
}
